import { isDev } from '@/lib/utils';

export type Trigger = 'BUTTON' | 'DELAY' | 'ON_CLOSE';
export type ComponentState = 'talking' | 'idle' | 'modal';

export interface TourButton {
  label: string;
  navigatePath?: string;
  devNavigatePath?: string;
  targetStepId?: string;
  isDynamicUsername?: boolean;
  restartTour?: boolean;
  forceReload?: boolean;
}

export interface TourStep {
  id: string;
  speechBubble?: string;
  componentState: ComponentState;
  trigger: Trigger;
  delayMs?: number;
  buttons?: TourButton[];
  nextStepId?: string;
  pausedCurrentSection?: string;
  pausedNextHabitat?: string;
  navigationPath?: string;
  devNavigationPath?: string;
}
export const TOUR_IS_DEV = isDev();

export const TOUR_STEPS: TourStep[] = [
  {
    id: '0-modal',
    componentState: 'modal',
    trigger: 'BUTTON',
    nextStepId: 'Home 1',
    pausedCurrentSection: 'Departure',
    pausedNextHabitat: 'Treetops',
  },
  {
    id: 'Home 1',
    speechBubble:
      "Welcome to our home base - where you can catch the latest live happenings across all the zoos! We'll check back in here so I can show you some hidden gems. This is where it all starts.",
    componentState: 'talking',
    trigger: 'DELAY',
    delayMs: 8000,
    nextStepId: 'Home 2',
    pausedCurrentSection: 'Departure',
    pausedNextHabitat: 'Treetops',
    navigationPath: '/home',
  },
  {
    id: 'Home 2',
    speechBubble:
      "The first stop on our tour is ... Orangutans! Let's head over to the treetops and meet 4-year-old Wali. He's sort of a celebrity around here. Are you ready?",
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      {
        label: 'Take me to the treetops!',
        navigatePath: '/h/toronto-zoo/sumatran-orangutans?tab=watch&id=699f40177c890cb0e7aec70a',
        devNavigatePath: '/h/toronto-zoo/test-habitat1?tab=watch&card=album&id=6985edd47e9d38372a13fde9',
      },
    ],
    nextStepId: 'O1',
    pausedCurrentSection: 'Departure',
    pausedNextHabitat: 'Treetops',
    navigationPath: '/home',
  },
  {
    id: 'O1',
    componentState: 'idle',
    trigger: 'ON_CLOSE',
    nextStepId: 'O2',
    pausedCurrentSection: 'Treetops',
    pausedNextHabitat: 'Grasslands',
    navigationPath: '/h/toronto-zoo/sumatran-orangutans?tab=watch&id=699f40177c890cb0e7aec70a',
    devNavigationPath: '/h/toronto-zoo/test-habitat1?tab=watch&card=album&id=6985edd47e9d38372a13fde9',
  },
  {
    id: 'O2',
    speechBubble:
      "What's amazing about habitats on zoolife is not only seeing top highlights, but also tracing their day-to-day journeys, almost like we get to live alongside them.",
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      { label: 'Explore Around', targetStepId: 'O3' },
      {
        label: "See Wali's Timeline",
        navigatePath: '/h/toronto-zoo/sumatran-orangutans?tab=watch&card=album',
        devNavigatePath: '/h/toronto-zoo/test-habitat1?tab=watch&card=album',
        targetStepId: 'O3',
      },
    ],
    nextStepId: 'O3',
    pausedCurrentSection: 'Treetops',
    pausedNextHabitat: 'Grasslands',
    navigationPath: '/h/toronto-zoo/sumatran-orangutans',
    devNavigationPath: '/h/toronto-zoo/test-habitat1',
  },
  {
    id: 'O3',
    speechBubble:
      'You can keep getting to know Wali here in his habitat! Try giving a 💛 to the videos his fans have clipped! Everything you save goes in "My Journal" as your personal souvenir from today\'s visit.',
    componentState: 'talking',
    trigger: 'DELAY',
    delayMs: 8000,
    nextStepId: 'O4',
    pausedCurrentSection: 'Treetops',
    pausedNextHabitat: 'Grasslands',
    navigationPath: '/h/toronto-zoo/sumatran-orangutans',
    devNavigationPath: '/h/toronto-zoo/test-habitat1',
  },
  {
    id: 'O4',
    speechBubble:
      "Another special animal species is the Maned Wolf. Let me know whenever you're ready to meet Lucky, a beloved ambassador of the grasslands! Take your time. No rush.",
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      {
        label: 'To the Grasslands',
        navigatePath: '/h/endangered-wolf-center/maned-wolves',
        devNavigatePath: '/h/lone-pine-koala-sanctuary/wolf',
      },
    ],
    nextStepId: 'MW1',
    pausedCurrentSection: 'Treetops',
    pausedNextHabitat: 'Grasslands',
    navigationPath: '/h/toronto-zoo/sumatran-orangutans',
    devNavigationPath: '/h/toronto-zoo/test-habitat1',
  },
  {
    id: 'MW1',
    speechBubble:
      'Welcome to the Grasslands where the rare South American Maned Wolves lead graceful lives. Let me introduce you to Lucky and her friend Luigi.',
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      {
        label: "What's Lucky's Story?",
        navigatePath: '/h/endangered-wolf-center/maned-wolves?tab=watch&id=675cad840782f32af2c67e30',
        devNavigatePath: '/h/lone-pine-koala-sanctuary/wolf?tab=watch&id=693b3757b02cbc814c5a0a24',
      },
    ],
    nextStepId: 'MW2',
    pausedCurrentSection: 'Grasslands',
    pausedNextHabitat: 'Playground',
    navigationPath: '/h/endangered-wolf-center/maned-wolves',
    devNavigationPath: '/h/lone-pine-koala-sanctuary/wolf',
  },
  {
    id: 'MW2',
    componentState: 'idle',
    trigger: 'ON_CLOSE',
    nextStepId: 'MW3',
    pausedCurrentSection: 'Grasslands',
    pausedNextHabitat: 'Playground',
    navigationPath: '/h/endangered-wolf-center/maned-wolves?tab=watch&id=675cad840782f32af2c67e30',
    devNavigationPath: '/h/lone-pine-koala-sanctuary/wolf?tab=watch&id=693b3757b02cbc814c5a0a24',
  },
  {
    id: 'MW3',
    speechBubble:
      "Maned wolves are so special, they are not a fox nor a wolf. That's why Lucky's breeding program is run across accredited zoos to support the health of her species. What did you think of her story?",
    componentState: 'talking',
    trigger: 'DELAY',
    delayMs: 10000,
    nextStepId: 'MW4',
    pausedCurrentSection: 'Grasslands',
    pausedNextHabitat: 'Playground',
    navigationPath: '/h/endangered-wolf-center/maned-wolves',
    devNavigationPath: '/h/lone-pine-koala-sanctuary/wolf',
  },
  {
    id: 'MW4',
    speechBubble:
      "Today, Lucky and Luigi's love story is still unfolding day by day... Will their breeding program goals be successful? If you ask me, Lucky really likes to play hard to get!",
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      {
        label: 'Show me!',
        navigatePath: '/h/endangered-wolf-center/maned-wolves?tab=watch&card=album&id=69a9ced4e1951ac0d908781b',
        devNavigatePath: '/h/lone-pine-koala-sanctuary/wolf?tab=watch&id=67b794d8b30ee5b1fbfc8fc0',
      },
    ],
    nextStepId: 'MW5',
    pausedCurrentSection: 'Grasslands',
    pausedNextHabitat: 'Playground',
    navigationPath: '/h/endangered-wolf-center/maned-wolves',
    devNavigationPath: '/h/lone-pine-koala-sanctuary/wolf',
  },
  {
    id: 'MW5',
    componentState: 'idle',
    trigger: 'ON_CLOSE',
    nextStepId: 'MW6',
    pausedCurrentSection: 'Grasslands',
    pausedNextHabitat: 'Playground',
    navigationPath: '/h/endangered-wolf-center/maned-wolves?tab=watch&card=album&id=69a9ced4e1951ac0d908781b',
    devNavigationPath: '/h/lone-pine-koala-sanctuary/wolf?tab=watch&id=67b794d8b30ee5b1fbfc8fc0',
  },
  {
    id: 'MW6',
    speechBubble: 'Can you tell Lucky and Luigi apart?',
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      {
        label: "What's the Secret?",
        navigatePath: '/h/endangered-wolf-center/maned-wolves?tab=info',
        devNavigatePath: '/h/lone-pine-koala-sanctuary/wolf?tab=info',
        targetStepId: 'MW7',
      },
    ],
    nextStepId: 'MW7',
    pausedCurrentSection: 'Grasslands',
    pausedNextHabitat: 'Playground',
    navigationPath: '/h/endangered-wolf-center/maned-wolves?tab=info',
    devNavigationPath: '/h/lone-pine-koala-sanctuary/wolf?tab=info',
  },
  {
    id: 'MW7',
    speechBubble:
      "There's a full guide in every habitat. Personally, I think Lucky's ears flop a bit more than Luigi's ears! Can you tell?",
    componentState: 'talking',
    trigger: 'DELAY',
    delayMs: 8000,
    nextStepId: 'MW8',
    pausedCurrentSection: 'Grasslands',
    pausedNextHabitat: 'Playground',
    navigationPath: '/h/endangered-wolf-center/maned-wolves',
    devNavigationPath: '/h/lone-pine-koala-sanctuary/wolf',
  },
  {
    id: 'MW8',
    speechBubble:
      "Breeding programs lead to amazing baby moments and milestones. Want to see my faves as we make our way to our next stop? You name the pace though, let's hang out here more if you'd like!",
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      {
        label: "I'm ready to keep going!",
        navigatePath: '/home',
      },
    ],
    nextStepId: 'BB1',
    pausedCurrentSection: 'Grasslands',
    pausedNextHabitat: 'Playground',
    navigationPath: '/h/endangered-wolf-center/maned-wolves',
    devNavigationPath: '/h/lone-pine-koala-sanctuary/wolf',
  },
  {
    id: 'BB1',
    speechBubble:
      "We're back on the home page now. Let me show you this playlist of baby lions, baby leopards, and baby monkeys growing up right in front of our eyes.",
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      {
        label: 'Show me the zoolife babies!',
        navigatePath: '/home?playlistId=6970d986d23fc76d59c34a19&mediaId=65cd40afedf5be233aac21bc',
        devNavigatePath: '/home?playlistId=67587b3bde2dc22bdd6b3917&mediaId=69a6ff88bacf02cae5add165',
        // Force reload so HomeContent re-initializes from URL params.
        forceReload: true,
      },
    ],
    nextStepId: 'BB2',
    pausedCurrentSection: 'Playground',
    pausedNextHabitat: 'Mountains',
    navigationPath: '/home?playlistId=6970d986d23fc76d59c34a19&mediaId=65cd40afedf5be233aac21bc',
    devNavigationPath: '/home?playlistId=67587b3bde2dc22bdd6b3917&mediaId=69a6ff88bacf02cae5add165',
  },
  {
    id: 'BB2',
    componentState: 'idle',
    trigger: 'ON_CLOSE',
    nextStepId: 'BB3',
    pausedCurrentSection: 'Playground',
    pausedNextHabitat: 'Mountains',
    // Keep full URL so go-back can reopen the home modal state.
    navigationPath: '/home?playlistId=6970d986d23fc76d59c34a19&mediaId=65cd40afedf5be233aac21bc',
    devNavigationPath: '/home?playlistId=67587b3bde2dc22bdd6b3917&mediaId=69a6ff88bacf02cae5add165',
  },
  {
    id: 'BB3',
    speechBubble:
      'On zoolife you can catch both the arrival of newborns and the early weeks & months that follow. Watching their first feedings, friendships... first swims, sprints, and even tumbles! Can you relate?',
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      { label: 'I remember when I did that as a baby!', targetStepId: 'BB4' },
      { label: 'No memories from when I was a baby!', targetStepId: 'BB4' },
    ],
    nextStepId: 'BB4',
    pausedCurrentSection: 'Playground',
    pausedNextHabitat: 'Mountains',
    navigationPath: '/home',
  },
  {
    id: 'BB4',
    speechBubble:
      'Before I forget, if you click on "Follow", you get updates on that animal\'s ongoing story! Okay, we should make our way to meet some of the keepers and care teams behind the scenes. No rush.',
    componentState: 'talking',
    trigger: 'DELAY',
    delayMs: 8000,
    nextStepId: 'BB5',
    pausedCurrentSection: 'Playground',
    pausedNextHabitat: 'Mountains',
    navigationPath: '/home',
  },
  {
    id: 'BB5',
    speechBubble:
      "Let's wander down the scenic path... It's so worth it! I'll show you some star animals on the way, along with some of my weird and quirky favourites!",
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      {
        label: 'Take me to the Mountains!',
        navigatePath: '/h/toronto-zoo/snow-leopard',
        devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
      },
    ],
    nextStepId: 'SL1',
    pausedCurrentSection: 'Mountains',
    pausedNextHabitat: 'Rivers',
    navigationPath: '/home',
  },
  {
    id: 'SL1',
    speechBubble:
      'Welcome to the Mountains! For some animals like snow leopards, we have not one, but two habitats on zoolife! Can you guess why?',
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      { label: 'To see them in different time zones', targetStepId: 'SL2' },
      { label: 'Because they are notoriously good at hiding', targetStepId: 'SL2' },
    ],
    nextStepId: 'SL2',
    pausedCurrentSection: 'Mountains',
    pausedNextHabitat: 'Rivers',
    navigationPath: '/h/toronto-zoo/snow-leopard',
    devNavigationPath: '/h/lone-pine-koala-sanctuary/koalas',
  },
  {
    id: 'SL2',
    speechBubble:
      "Both are correct! Zoolife livestreams from multiple camera angles, and from different time zones around the world. If they're not visible now on the live cameras, catch up on recent video sightings!",
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      {
        label: "Let's see some whiskers!",
        navigatePath: '/h/toronto-zoo/snow-leopard?tab=watch&card=album&id=69949b4d0702216d4e537ba5',
        devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas?tab=watch&card=album&id=65ae9da40d2611b066000f0e',
      },
    ],
    nextStepId: 'SL3',
    pausedCurrentSection: 'Mountains',
    pausedNextHabitat: 'Rivers',
    navigationPath: '/h/toronto-zoo/snow-leopard',
    devNavigationPath: '/h/lone-pine-koala-sanctuary/koalas',
  },
  {
    id: 'SL3',
    componentState: 'idle',
    trigger: 'ON_CLOSE',
    nextStepId: 'SL4',
    pausedCurrentSection: 'Mountains',
    pausedNextHabitat: 'Rivers',
    navigationPath: '/h/toronto-zoo/snow-leopard?tab=watch&card=album&id=69949b4d0702216d4e537ba5',
    devNavigationPath: '/h/lone-pine-koala-sanctuary/koalas?tab=watch&card=album&id=65ae9da40d2611b066000f0e',
  },
  {
    id: 'SL4',
    speechBubble:
      "There's something about watching animals move at their own pace that makes everything else feel a little quieter... What do you think?",
    componentState: 'talking',
    trigger: 'DELAY',
    delayMs: 15000,
    buttons: [{ label: 'I feel relaxed', targetStepId: 'SL5' }],
    nextStepId: 'SL5',
    pausedCurrentSection: 'Mountains',
    pausedNextHabitat: 'Rivers',
    navigationPath: '/h/toronto-zoo/snow-leopard',
    devNavigationPath: '/h/lone-pine-koala-sanctuary/koalas',
  },
  {
    id: 'SL5',
    speechBubble:
      'Snow leopards are incredible creatures. While there are many "big cats", snow leopards are the most elusive of them all. Why do you think some animals like to hide?',
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      { label: "It's an adaptation to their environment", targetStepId: 'SL6' },
      { label: "I don't know, but I can relate...", targetStepId: 'SL6' },
    ],
    nextStepId: 'SL6',
    pausedCurrentSection: 'Mountains',
    pausedNextHabitat: 'Rivers',
    navigationPath: '/h/toronto-zoo/snow-leopard',
    devNavigationPath: '/h/lone-pine-koala-sanctuary/koalas',
  },
  {
    id: 'SL6',
    speechBubble:
      'What environmental factors might make us humans want to hide sometimes? Journal down some ideas. Which ones apply to you?',
    componentState: 'talking',
    trigger: 'DELAY',
    delayMs: 15000,
    buttons: [{ label: "I'm finished answering", targetStepId: 'SL7' }],
    nextStepId: 'SL7',
    pausedCurrentSection: 'Mountains',
    pausedNextHabitat: 'Rivers',
    navigationPath: '/h/toronto-zoo/snow-leopard',
    devNavigationPath: '/h/lone-pine-koala-sanctuary/koalas',
  },
  {
    id: 'SL7',
    speechBubble:
      "If you're feeling curious, each habitat has a little quiz filled with fun facts! Do you know the common nickname for this elusive species?",
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      {
        label: 'Quiz me!',
        navigatePath: '/h/toronto-zoo/snow-leopard?tab=learn',
        devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas?tab=learn',
        targetStepId: 'SL8',
      },
      { label: 'No thanks', targetStepId: 'SL9' },
    ],
    nextStepId: 'SL8',
    pausedCurrentSection: 'Mountains',
    pausedNextHabitat: 'Rivers',
    navigationPath: '/h/toronto-zoo/snow-leopard',
    devNavigationPath: '/h/lone-pine-koala-sanctuary/koalas',
  },
  {
    id: 'SL8',
    componentState: 'idle',
    trigger: 'DELAY',
    delayMs: 30000,
    nextStepId: 'SL9',
    pausedCurrentSection: 'Mountains',
    pausedNextHabitat: 'Rivers',
    navigationPath: '/h/toronto-zoo/snow-leopard?tab=learn',
    devNavigationPath: '/h/lone-pine-koala-sanctuary/koalas?tab=learn',
  },
  {
    id: 'SL9',
    speechBubble:
      'This next animal coming up loves hanging out by the water! Let me know when you\'re ready. Stay with the "snowballs" as long as you like.',
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      {
        label: 'Come down to the river',
        navigatePath: '/h/akron-zoo/north-american-river-otters',
        devNavigatePath: '/h/zoolife-office/north-american-river-otters',
      },
    ],
    nextStepId: 'RO1',
    pausedCurrentSection: 'Rivers',
    pausedNextHabitat: 'Forest',
    navigationPath: '/h/toronto-zoo/snow-leopard',
    devNavigationPath: '/h/lone-pine-koala-sanctuary/koalas',
  },
  {
    id: 'RO1',
    speechBubble:
      "Otters spend most of their day in motion — weaving through water, diving, and sliding along the banks. I find it's one of our most playful habitats on zoolife.",
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      {
        label: "Let's see them swim!",
        navigatePath: '/h/akron-zoo/north-american-river-otters?tab=watch&id=6999ca83e1951ac0d966abb7',
        devNavigatePath: '/h/zoolife-office/north-american-river-otters?tab=watch&id=68b077a218c3b5b9afce6fa5',
      },
    ],
    nextStepId: 'RO2',
    pausedCurrentSection: 'Rivers',
    pausedNextHabitat: 'Forest',
    navigationPath: '/h/akron-zoo/north-american-river-otters',
    devNavigationPath: '/h/zoolife-office/north-american-river-otters',
  },
  {
    id: 'RO2',
    componentState: 'idle',
    trigger: 'ON_CLOSE',
    nextStepId: 'RO3',
    pausedCurrentSection: 'Rivers',
    pausedNextHabitat: 'Forest',
    navigationPath: '/h/akron-zoo/north-american-river-otters?tab=watch&id=6999ca83e1951ac0d966abb7',
    devNavigationPath: '/h/zoolife-office/north-american-river-otters?tab=watch&id=68b077a218c3b5b9afce6fa5',
  },
  {
    id: 'RO3',
    speechBubble:
      'If you could go take a swim right now, would you pick a river, or a pond? Because our next animal loves a good puddle...',
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      { label: 'River (shallow)', targetStepId: 'RO4' },
      { label: 'Pond (deep)', targetStepId: 'RO4' },
    ],
    nextStepId: 'RO4',
    pausedCurrentSection: 'Rivers',
    pausedNextHabitat: 'Forest',
    navigationPath: '/h/akron-zoo/north-american-river-otters',
    devNavigationPath: '/h/zoolife-office/north-american-river-otters',
  },
  {
    id: 'RO4',
    speechBubble:
      "Let's slow down and hang out with the otters for a little longer. When you're ready, we can head over to see the big puddle splashers.",
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      {
        label: 'On to the forests',
        navigatePath: '/h/great-plains-zoo/coastal-brown-bears',
        devNavigatePath: '/h/toronto-zoo/tigers',
      },
    ],
    nextStepId: 'B1',
    pausedCurrentSection: 'Rivers',
    pausedNextHabitat: 'Forest',
    navigationPath: '/h/akron-zoo/north-american-river-otters',
    devNavigationPath: '/h/zoolife-office/north-american-river-otters',
  },
  {
    id: 'B1',
    speechBubble:
      'Bears have a rhythm all their own. Sometimes they roam, sometimes they splash, and sometimes they simply rest in the sun.',
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      {
        label: 'Show me a splash!',
        navigatePath:
          '/h/great-plains-zoo/coastal-brown-bears?tab=watch&card=album&search=splash&id=68dd4705cb9617ee92cba9c4',
        devNavigatePath: '/h/zoolife-office/north-american-river-otters?tab=watch&card=album&id=693b04697f513c2f1294d5b3',
      },
    ],
    nextStepId: 'B2',
    pausedCurrentSection: 'Forest',
    pausedNextHabitat: 'Skies',
    navigationPath: '/h/great-plains-zoo/coastal-brown-bears',
    devNavigationPath: '/h/toronto-zoo/tigers',
  },
  {
    id: 'B2',
    componentState: 'idle',
    trigger: 'ON_CLOSE',
    nextStepId: 'B3',
    pausedCurrentSection: 'Forest',
    pausedNextHabitat: 'Skies',
    navigationPath: '/h/great-plains-zoo/coastal-brown-bears?tab=watch&card=album&search=splash&id=68dd4705cb9617ee92cba9c4',
    devNavigationPath: '/h/zoolife-office/north-american-river-otters?tab=watch&card=album&id=693b04697f513c2f1294d5b3',
  },
  {
    id: 'B3',
    speechBubble:
      "Watching them relax reminds me it's important to slow down when things are moving too fast... Breathe in slowly, then try letting out a big sigh.",
    componentState: 'talking',
    trigger: 'DELAY',
    delayMs: 20000,
    nextStepId: 'B4',
    pausedCurrentSection: 'Forest',
    pausedNextHabitat: 'Skies',
    navigationPath: '/h/great-plains-zoo/coastal-brown-bears',
    devNavigationPath: '/h/zoolife-office/north-american-river-otters',
  },
  {
    id: 'B4',
    speechBubble: "Ok, let's get a little weirder. Are you ready for something different?",
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      {
        label: "Let's flutter over!",
        navigatePath: '/h/butterfly-pavilion/chrysalis-chamber',
        devNavigatePath: '/h/zoolife-office/office',
      },
      {
        label: 'Hmmm...',
        navigatePath: '/h/butterfly-pavilion/chrysalis-chamber',
        devNavigatePath: '/h/zoolife-office/office',
      },
    ],
    nextStepId: 'CC1',
    pausedCurrentSection: 'Forest',
    pausedNextHabitat: 'Skies',
    navigationPath: '/h/great-plains-zoo/coastal-brown-bears',
    devNavigationPath: '/h/zoolife-office/north-american-river-otters',
  },
  {
    id: 'CC1',
    speechBubble:
      'Inside these chrysalises, also called cocoons, butterflies are transforming! If the timing is right, we might even see one emerge.',
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      {
        label: 'Show me!',
        navigatePath: '/h/butterfly-pavilion/chrysalis-chamber?tab=watch&id=6828ada919ce5503dc9b18d9',
        devNavigatePath: '/h/zoolife-office/office?tab=watch&card=album&id=69a8d533b496f33c58b76cf5',
      },
    ],
    nextStepId: 'CC2',
    pausedCurrentSection: 'Skies',
    pausedNextHabitat: 'The Undergrowth',
    navigationPath: '/h/butterfly-pavilion/chrysalis-chamber',
    devNavigationPath: '/h/zoolife-office/office',
  },
  {
    id: 'CC2',
    componentState: 'idle',
    trigger: 'ON_CLOSE',
    nextStepId: 'CC3',
    pausedCurrentSection: 'Skies',
    pausedNextHabitat: 'The Undergrowth',
    navigationPath: '/h/butterfly-pavilion/chrysalis-chamber?tab=watch&id=6828ada919ce5503dc9b18d9',
    devNavigationPath: '/h/zoolife-office/office?tab=watch&card=album&id=69a8d533b496f33c58b76cf5',
  },
  {
    id: 'CC3',
    speechBubble:
      "The chrysalis chamber based out of the butterfly pavilion. How unique was that? We're seeing the magic of nature in real time!",
    componentState: 'talking',
    trigger: 'DELAY',
    delayMs: 10000,
    nextStepId: 'CC4',
    pausedCurrentSection: 'Skies',
    pausedNextHabitat: 'The Undergrowth',
    navigationPath: '/h/butterfly-pavilion/chrysalis-chamber',
    devNavigationPath: '/h/zoolife-office/office',
  },
  {
    id: 'CC4',
    speechBubble:
      'For mammals, more private care is often involved, but here we can see butterflies unfurl their wings for the first time.',
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      { label: 'Fascinating!', targetStepId: 'CC5' },
      { label: 'Not for me', targetStepId: 'CC6' },
    ],
    nextStepId: 'CC5',
    pausedCurrentSection: 'Skies',
    pausedNextHabitat: 'The Undergrowth',
    navigationPath: '/h/butterfly-pavilion/chrysalis-chamber',
    devNavigationPath: '/h/zoolife-office/office',
  },
  {
    id: 'CC5',
    speechBubble: 'You and me both! Since you thought that was cool, let me take you to another hidden gem...',
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      {
        label: 'Show me!',
        navigatePath: '/h/butterfly-pavilion/leafcutter-ants',
        devNavigatePath: '/h/zoolife-office/office',
        targetStepId: 'ANT1',
      },
    ],
    nextStepId: 'ANT1',
    pausedCurrentSection: 'Skies',
    pausedNextHabitat: 'The Undergrowth',
    navigationPath: '/h/butterfly-pavilion/chrysalis-chamber',
    devNavigationPath: '/h/zoolife-office/office',
  },
  {
    id: 'CC6',
    speechBubble:
      "That's cool, not everyone likes insects. We do have some other small yet mighty bugs! Would you like to check them out, or head to the Bayou instead?",
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      {
        label: "Let's see some bugs!",
        navigatePath: '/h/butterfly-pavilion/leafcutter-ants',
        devNavigatePath: '/h/zoolife-office/office',
        targetStepId: 'ANT1',
      },
      {
        label: 'Take me to the Bayou',
        navigatePath: '/h/phoenix-herpetological-sanctuary/american-alligators',
        devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
        targetStepId: 'ALL1',
      },
    ],
    nextStepId: 'ANT1',
    pausedCurrentSection: 'Skies',
    pausedNextHabitat: 'The Undergrowth',
    navigationPath: '/h/butterfly-pavilion/chrysalis-chamber',
    devNavigationPath: '/h/zoolife-office/office',
  },
  {
    id: 'ANT1',
    speechBubble:
      "These ants are always on a mission. Let's follow one carrying a leaf many times its size back to its colony.",
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      {
        label: 'Show me!',
        navigatePath: '/h/butterfly-pavilion/leafcutter-ants?tab=watch&id=697e6eb70702216d4e178345',
        devNavigatePath: '/h/zoolife-office/office?tab=watch&card=album&id=69a8d50fbaa80ed0cb07235a',
      },
    ],
    nextStepId: 'ANT2',
    pausedCurrentSection: 'The Undergrowth',
    pausedNextHabitat: 'Bayou',
    navigationPath: '/h/butterfly-pavilion/leafcutter-ants',
    devNavigationPath: '/h/zoolife-office/office',
  },
  {
    id: 'ANT2',
    componentState: 'idle',
    trigger: 'ON_CLOSE',
    nextStepId: 'ANT3',
    pausedCurrentSection: 'The Undergrowth',
    pausedNextHabitat: 'Bayou',
    navigationPath: '/h/butterfly-pavilion/leafcutter-ants?tab=watch&id=697e6eb70702216d4e178345',
    devNavigationPath: '/h/zoolife-office/office?tab=watch&card=album&id=69a8d50fbaa80ed0cb07235a',
  },
  {
    id: 'ANT3',
    speechBubble:
      'Ants remind me of the power of intention. They are so dedicated and present for their team goals. In their case... chomping up some leaves to bring home!',
    componentState: 'talking',
    trigger: 'DELAY',
    delayMs: 10000,
    nextStepId: 'ANT4',
    pausedCurrentSection: 'The Undergrowth',
    pausedNextHabitat: 'Bayou',
    navigationPath: '/h/butterfly-pavilion/leafcutter-ants',
    devNavigationPath: '/h/zoolife-office/office',
  },
  {
    id: 'ANT4',
    speechBubble: 'Ready for our last pit-stop before we meet the keepers and caretakers behind the scenes?',
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      {
        label: 'To the Bayou',
        navigatePath: '/h/phoenix-herpetological-sanctuary/american-alligators',
        devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
      },
    ],
    nextStepId: 'ALL1',
    pausedCurrentSection: 'The Undergrowth',
    pausedNextHabitat: 'Bayou',
    navigationPath: '/h/butterfly-pavilion/leafcutter-ants',
    devNavigationPath: '/h/zoolife-office/office',
  },
  {
    id: 'ALL1',
    speechBubble: 'This habitat moves at a slower pace. Reptiles spend a LOT of time soaking up warmth from the sun.',
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      {
        label: 'Show me!',
        navigatePath: '/h/phoenix-herpetological-sanctuary/american-alligators?tab=watch&id=668d408d5dbe678c23fecdf2',
        devNavigatePath: '/h/zoolife-office/north-american-river-otters?tab=watch&card=album&id=695d840e7fa9f43d1977fa5c',
      },
    ],
    nextStepId: 'ALL2',
    pausedCurrentSection: 'Bayou',
    pausedNextHabitat: 'Pacific Coast',
    navigationPath: '/h/phoenix-herpetological-sanctuary/american-alligators',
    devNavigationPath: '/h/lone-pine-koala-sanctuary/koalas',
  },
  {
    id: 'ALL2',
    componentState: 'idle',
    trigger: 'ON_CLOSE',
    nextStepId: 'ALL3',
    pausedCurrentSection: 'Bayou',
    pausedNextHabitat: 'Pacific Coast',
    navigationPath: '/h/phoenix-herpetological-sanctuary/american-alligators?tab=watch&id=668d408d5dbe678c23fecdf2',
    devNavigationPath: '/h/zoolife-office/north-american-river-otters?tab=watch&card=album&id=695d840e7fa9f43d1977fa5c',
  },
  {
    id: 'ALL3',
    speechBubble:
      'These reptiles really know how to relax. What bliss! Since they are cool-blooded, they can sit in the sun for so much longer than humans.',
    componentState: 'talking',
    trigger: 'DELAY',
    delayMs: 10000,
    nextStepId: 'ALL4',
    pausedCurrentSection: 'Bayou',
    pausedNextHabitat: 'Pacific Coast',
    navigationPath: '/h/phoenix-herpetological-sanctuary/american-alligators',
    devNavigationPath: '/h/zoolife-office/north-american-river-otters',
  },
  {
    id: 'ALL4',
    speechBubble:
      'Alligators are one of the most impressive reptiles. If you had an afternoon to relax on a sunny day, would you be in the sun like the alligator or in the pool like a bear?',
    componentState: 'talking',
    trigger: 'DELAY',
    delayMs: 10000,
    buttons: [
      { label: 'In the sun', targetStepId: 'ALL5' },
      { label: 'In the pool', targetStepId: 'ALL5' },
    ],
    nextStepId: 'ALL5',
    pausedCurrentSection: 'Bayou',
    pausedNextHabitat: 'Pacific Coast',
    navigationPath: '/h/phoenix-herpetological-sanctuary/american-alligators',
    devNavigationPath: '/h/zoolife-office/north-american-river-otters',
  },
  {
    id: 'ALL5',
    speechBubble:
      'I\'m a sloth, and they say we\'re the "goldilocks" animals when it comes to temperature! 😇  Ready to see how keepers take care of their animals? This next animal loves fish.',
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      {
        label: 'Take me to the Pacific Coast',
        navigatePath: '/h/santa-barbara/humboldt-penguin',
        devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
      },
    ],
    nextStepId: 'P1',
    pausedCurrentSection: 'Bayou',
    pausedNextHabitat: 'Pacific Coast',
    navigationPath: '/h/phoenix-herpetological-sanctuary/american-alligators',
    devNavigationPath: '/h/zoolife-office/north-american-river-otters',
  },
  {
    id: 'P1',
    speechBubble:
      'Here on the Pacific Coast, the zoo staff and keepers make sure these penguins have all the food, toys, and shelter they can enjoy.',
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      {
        label: 'Replay a Keeper Chat',
        navigatePath: '/h/santa-barbara/humboldt-penguin?tab=watch&card=talks&id=6992cd06d9ad206d3b911aeb',
        devNavigatePath: '/h/lone-pine-koala-sanctuary/wolf?tab=watch&id=67b794d8b30ee5b1fbfc8fc0',
      },
    ],
    nextStepId: 'P2',
    pausedCurrentSection: 'Pacific Coast',
    pausedNextHabitat: 'Behind the Scenes',
    navigationPath: '/h/santa-barbara/humboldt-penguin',
    devNavigationPath: '/h/lone-pine-koala-sanctuary/koalas',
  },
  {
    id: 'P2',
    componentState: 'idle',
    trigger: 'ON_CLOSE',
    nextStepId: 'P3',
    pausedCurrentSection: 'Pacific Coast',
    pausedNextHabitat: 'Behind the Scenes',
    navigationPath: '/h/santa-barbara/humboldt-penguin?tab=watch&card=talks&id=6992cd06d9ad206d3b911aeb',
    devNavigationPath: '/h/lone-pine-koala-sanctuary/wolf?tab=watch&id=67b794d8b30ee5b1fbfc8fc0',
  },
  {
    id: 'P3',
    speechBubble:
      'Each habitat has a dedicated team taking care of the animals. Each keeper has different responsibilities based on their expertise and the animal. Leaving puzzles, games, scent trails...',
    componentState: 'talking',
    trigger: 'DELAY',
    delayMs: 15000,
    buttons: [
      { label: 'What a cool job!', targetStepId: 'P4' },
      { label: 'That sounds like hard work!', targetStepId: 'P4' },
    ],
    nextStepId: 'P4',
    pausedCurrentSection: 'Pacific Coast',
    pausedNextHabitat: 'Behind the Scenes',
    navigationPath: '/h/santa-barbara/humboldt-penguin',
    devNavigationPath: '/h/lone-pine-koala-sanctuary/wolf',
  },
  {
    id: 'P4',
    speechBubble:
      "They also join us directly on zoolife with their latest animal news, right from the scene. I'll show you on our way to our last stop, whenever you're ready",
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      {
        label: 'How do I Join a Keeper Talk?',
        navigatePath: '/home',
      },
    ],
    nextStepId: 'V1',
    pausedCurrentSection: 'Pacific Coast',
    pausedNextHabitat: 'Behind the Scenes',
    navigationPath: '/h/santa-barbara/humboldt-penguin',
    devNavigationPath: '/h/lone-pine-koala-sanctuary/wolf',
  },
  {
    id: 'V1',
    speechBubble:
      "On the right, you'll see Upcoming Events. Click on the 🔔 to be reminded to join to ask keepers questions directly, or simply enjoy these laid-back chats as a zoolife member!",
    componentState: 'talking',
    trigger: 'DELAY',
    delayMs: 15000,
    nextStepId: 'V2',
    pausedCurrentSection: 'Behind the Scenes',
    pausedNextHabitat: 'Ocean',
    navigationPath: '/home',
  },
  {
    id: 'V2',
    speechBubble:
      'Keep checking back on zoolife because some keepers will bring us LIVE behind the scenes for big feedings or routine vet appointments.',
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      {
        label: 'Show Me!',
        navigatePath: '/h/safari-west/reticulated-giraffe?tab=watch&id=680f2f8d6dd68e9c42a9ac5c',
        devNavigatePath: '/h/toronto-zoo/test-habitat1?tab=watch&card=album&id=6985eb010594f4cb5c929eef',
      },
    ],
    nextStepId: 'V3',
    pausedCurrentSection: 'Behind the Scenes',
    pausedNextHabitat: 'Ocean',
    navigationPath: '/home',
  },
  {
    id: 'V3',
    componentState: 'idle',
    trigger: 'ON_CLOSE',
    nextStepId: 'V4',
    pausedCurrentSection: 'Behind the Scenes',
    pausedNextHabitat: 'Ocean',
    navigationPath: '/h/safari-west/reticulated-giraffe?tab=watch&id=680f2f8d6dd68e9c42a9ac5c',
    devNavigationPath: '/h/toronto-zoo/test-habitat1?tab=watch&card=album&id=6985eb010594f4cb5c929eef',
  },
  {
    id: 'V4',
    speechBubble: "Alright, one more stop before we reach the end of our tour. Let's catch a wave!",
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      {
        label: 'Take me to the Ocean',
        navigatePath: '/h/clearwater-marine-aquarium/dolphins',
        devNavigatePath: '/h/zoolife-office/north-american-river-otters',
      },
    ],
    nextStepId: 'D1',
    pausedCurrentSection: 'Behind the Scenes',
    pausedNextHabitat: 'Ocean',
    // After V3 close, user is still on giraffe habitat; keep navigationPath aligned.
    navigationPath: '/h/safari-west/reticulated-giraffe',
    devNavigationPath: '/h/toronto-zoo/test-habitat1',
  },
  {
    id: 'D1',
    speechBubble: 'Say hello to our beautiful dolphins, some of the most playful and graceful animals in the ocean.',
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      {
        label: 'Show Me!',
        navigatePath: '/h/clearwater-marine-aquarium/dolphins?tab=watch&id=69a4a78fed609b37e66df52a',
        devNavigatePath: '/h/zoolife-office/north-american-river-otters?tab=watch&id=66f1d927a6c19bcc78346122',
      },
    ],
    nextStepId: 'D2',
    pausedCurrentSection: 'Ocean',
    pausedNextHabitat: 'The End',
    navigationPath: '/h/clearwater-marine-aquarium/dolphins',
    devNavigationPath: '/h/zoolife-office/north-american-river-otters',
  },
  {
    id: 'D2',
    componentState: 'idle',
    trigger: 'ON_CLOSE',
    nextStepId: 'D3',
    pausedCurrentSection: 'Ocean',
    pausedNextHabitat: 'The End',
    navigationPath: '/h/clearwater-marine-aquarium/dolphins?tab=watch&id=69a4a78fed609b37e66df52a',
    devNavigationPath: '/h/zoolife-office/north-american-river-otters?tab=watch&id=66f1d927a6c19bcc78346122',
  },
  {
    id: 'D3',
    speechBubble:
      'Aquatic habitats bring a completely different perspective — their movements are so peaceful! Try switching between the 4 different camera views in this habitat using the green bar.',
    componentState: 'talking',
    trigger: 'DELAY',
    delayMs: 30000,
    nextStepId: 'D4',
    pausedCurrentSection: 'Ocean',
    pausedNextHabitat: 'The End',
    navigationPath: '/h/clearwater-marine-aquarium/dolphins',
    devNavigationPath: '/h/zoolife-office/north-american-river-otters',
  },
  {
    id: 'D4',
    speechBubble:
      "Have you used the chat yet? If not, say hello or leave a reaction for the dolphin lovers here! You can click on community members' profiles to see what other people are checking out.",
    componentState: 'talking',
    trigger: 'DELAY',
    delayMs: 6000,
    nextStepId: 'D5',
    pausedCurrentSection: 'Ocean',
    pausedNextHabitat: 'The End',
    navigationPath: '/h/clearwater-marine-aquarium/dolphins',
    devNavigationPath: '/h/zoolife-office/north-american-river-otters',
  },
  {
    id: 'D5',
    speechBubble:
      "This tour gives you 24 hours of zoolife access. So there's no rush to leave. Let me take you back to home base when you're ready.",
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      {
        label: "Let's Wind Down",
        navigatePath: '/home',
      },
    ],
    nextStepId: 'End1',
    pausedCurrentSection: 'Ocean',
    pausedNextHabitat: 'The End',
    navigationPath: '/h/clearwater-marine-aquarium/dolphins',
    devNavigationPath: '/h/zoolife-office/north-american-river-otters',
  },
  {
    id: 'End1',
    speechBubble:
      "Congratulations, explorer! You just wandered through forests, grasslands, mountains, and ponds. Keep exploring for as long as you'd like.",
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      { label: "I'm done with my guided journey!", targetStepId: 'modal-end' },
      // Stay on End1 when opening help?tab=plans so the tour can resume from End1 afterward.
      { label: 'How do I become a zoolife member?', navigatePath: '/help?tab=plans' },
    ],
    pausedCurrentSection: 'Tour End',
    navigationPath: '/home',
  },
  {
    id: 'modal-end',
    componentState: 'modal',
    trigger: 'BUTTON',
    nextStepId: 'modal-end-submitted',
  },
  {
    id: 'modal-end-submitted',
    componentState: 'modal',
    trigger: 'BUTTON',
    nextStepId: 'End2',
  },
  {
    id: 'End2',
    speechBubble:
      'Remember, there\'s no "correct" way to watch zoolife! Your all-access pass lets you see everything for 24h. Tomorrow, the animals will still be here waiting for you to come back and revisit!',
    componentState: 'talking',
    trigger: 'BUTTON',
    buttons: [
      { label: 'Revisit the animals on my own', navigatePath: '/home' },
      { label: 'Restart the tour again', restartTour: true },
    ],
    pausedCurrentSection: 'Tour End',
    // End2 is intentionally route-agnostic so the 24h banner can show on any page.
  },
];

export const TOUR_STEP_MAP = new Map<string, TourStep>(TOUR_STEPS.map((step) => [step.id, step]));

export const getStep = (id: string): TourStep | undefined => TOUR_STEP_MAP.get(id);

// Keep this map in sync with TOUR_STEPS entry routes/step IDs.
export const NEXT_HABITAT_SKIP_MAP: Record<string, { stepId: string; navigatePath: string; devNavigatePath?: string }> = {
  Treetops: {
    stepId: 'O1',
    navigatePath: '/h/toronto-zoo/sumatran-orangutans?tab=watch&id=699f40177c890cb0e7aec70a',
    devNavigatePath: '/h/toronto-zoo/test-habitat1?tab=watch&card=album&id=6985edd47e9d38372a13fde9',
  },
  Grasslands: {
    stepId: 'MW1',
    navigatePath: '/h/endangered-wolf-center/maned-wolves',
    devNavigatePath: '/h/lone-pine-koala-sanctuary/wolf',
  },
  Playground: {
    stepId: 'BB1',
    navigatePath: '/home',
  },
  Mountains: {
    stepId: 'SL1',
    navigatePath: '/h/toronto-zoo/snow-leopard',
    devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
  },
  Rivers: {
    stepId: 'RO1',
    navigatePath: '/h/akron-zoo/north-american-river-otters',
    devNavigatePath: '/h/zoolife-office/north-american-river-otters',
  },
  Forest: {
    stepId: 'B1',
    navigatePath: '/h/great-plains-zoo/coastal-brown-bears',
    devNavigatePath: '/h/toronto-zoo/tigers',
  },
  Skies: {
    stepId: 'CC1',
    navigatePath: '/h/butterfly-pavilion/chrysalis-chamber',
    devNavigatePath: '/h/zoolife-office/office',
  },
  'The Undergrowth': {
    stepId: 'ANT1',
    navigatePath: '/h/butterfly-pavilion/leafcutter-ants',
    devNavigatePath: '/h/zoolife-office/office',
  },
  Bayou: {
    stepId: 'ALL1',
    navigatePath: '/h/phoenix-herpetological-sanctuary/american-alligators',
    devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
  },
  'Pacific Coast': {
    stepId: 'P1',
    navigatePath: '/h/santa-barbara/humboldt-penguin',
    devNavigatePath: '/h/lone-pine-koala-sanctuary/koalas',
  },
  'Behind the Scenes': {
    stepId: 'V1',
    navigatePath: '/home',
  },
  Ocean: {
    stepId: 'D1',
    navigatePath: '/h/clearwater-marine-aquarium/dolphins',
    devNavigatePath: '/h/zoolife-office/north-american-river-otters',
  },
  'The End': {
    stepId: 'End1',
    navigatePath: '/home',
  },
};
